import type { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core'
import { getAuth } from 'firebase/auth'
import { useAsyncData } from '#app'
import type { AsyncData } from '#app/composables/asyncData'

function getCookieValue(cookieName: string) {
  const cookies = document.cookie.split('; ')
  for (const cookie of cookies) {
    const [name, value] = cookie.split('=')
    if (name === cookieName) {
      return decodeURIComponent(value)
    }
  }
  return null // Return null if the cookie isn't found
}

function isPbcEnabled() {
  const bpcEnabled = typeof document !== 'undefined' ? getCookieValue('pbc') : ''
  return bpcEnabled === 'true'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function newQuery<T, V>(document: DocumentNode<T, V>, variables: any | null): { [key: string]: any } {
  const extensions = {
    persistedQuery: {
      version: 1,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      sha256Hash: document['__meta__']['hash'],
    },
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const queryVariables: { [key: string]: any } = {}
  if (variables) {
    for (const variable in variables) {
      const v = variables[variable]
      if (isRef(v)) {
        queryVariables[variable] = unref(v)
      }
      else {
        queryVariables[variable] = variables[variable]
      }
    }
  }
  return {
    extensions,
    variables: queryVariables,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function executeGraphQuery<T, V>(document: DocumentNode<T, V>, variables: any | null = null, authenticate: boolean = false, throwOnError: boolean = false) {
  const pbcEnabled = isPbcEnabled()
  const headers = new Headers()
  headers.set('Content-Type', 'application/json')
  headers.set('Accept', 'application/json')
  if (import.meta.server) {
    headers.set('Cache-Control', 'no-cache, no-store, must-revalidate')
  }
  if (pbcEnabled) {
    headers.set('X-PBC-Enabled', 'true')
  }
  const op = document.definitions.find(d => d.kind === 'OperationDefinition')
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const mutation = op?.operation === 'mutation'
  if (mutation && !import.meta.server) {
    authenticate = true
  }
  if (import.meta.server) {
    authenticate = false
  }
  if (authenticate) {
    const auth = getAuth()
    if (!auth.currentUser) {
      try {
        await auth.authStateReady()
      }
      catch (e) {
        console.error('failed to wait for auth state', e)
      }
    }
    const token = await auth.currentUser?.getIdToken()
    if (token) {
      headers.set('Authorization', 'Bearer ' + token)
    }
  }
  let url = useRuntimeConfig().meshUrl || '/graphql'
  if (import.meta.server) {
    url += '?_ts=' + (new Date().getTime())
  }
  const response = await $fetch(url.toString(), {
    headers: headers,
    query: !mutation ? newQuery(document, variables) : undefined,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    method: mutation ? 'POST' : 'GET',
    body: mutation ? JSON.stringify(newQuery(document, variables)) : undefined,
    cache: import.meta.server || authenticate || pbcEnabled ? 'no-cache' : undefined,
  })
  if (response) {
    if (response.errors && response.errors.length > 0 && throwOnError) {
      throw response.errors[0]
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response as any).data
  }
  return null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useGraphQuery<T, V>(document: DocumentNode<T, V>, variables: V | any | null = null, authenticate: boolean = false, immediate: boolean | undefined = undefined): AsyncData<T | null, any> {
  const refs = []
  if (variables) {
    for (const variable in variables) {
      const v = variables[variable]
      if (isRef(v)) {
        refs.push(v)
      }
    }
  }
  const key = '/graphql' + JSON.stringify(newQuery(document, variables))
  return useAsyncData(key, async (_) => {
    return executeGraphQuery(document, variables, authenticate)
  }, {
    watch: refs,
    deep: false,
    immediate: immediate,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as AsyncData<T | null, any>
}
